import { useCallback, useEffect, useMemo, useState } from 'react';
import { patientApi } from '../services/patientApi';
import { isIcuEntryOpen, sortIcuEntries } from '../utils/patientViewModel';

export const usePatientDetail = (patientId) => {
  const [patient, setPatient] = useState(null);
  const [uci, setUci] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enteredAt, setEnteredAt] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [discharging, setDischarging] = useState(false);
  const [toast, setToast] = useState(null);
  const [nuevoEvento, setNuevoEvento] = useState({ tipo: 'estado', descripcion: '' });
  const [department, setDepartment] = useState('');
  const [deptSaving, setDeptSaving] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const load = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const [patientRes, icuRes, eventsRes] = await Promise.all([
        patientApi.getPatientById(patientId),
        patientApi.getPatientIcuEntries(patientId),
        patientApi.getPatientEvents(patientId),
      ]);

      const nextPatient = patientRes.data;
      setPatient(nextPatient);
      setDepartment(nextPatient?.department || '');
      setUci(icuRes.data || []);
      setEventos(eventsRes.data || []);
    } catch (err) {
      setError('No se pudo cargar la información del paciente.');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load(true);
  }, [load]);

  const hasOpenEntry = useMemo(() => uci.some(isIcuEntryOpen), [uci]);
  const isDischarged = String(patient?.estado || '').toLowerCase() === 'alta';
  const sortedUci = useMemo(() => sortIcuEntries(uci), [uci]);

  const entrarUci = useCallback(async (event) => {
    event?.preventDefault?.();
    if (saving) return;
    if (isDischarged) {
      showToast('No se puede registrar una entrada a UCI a un paciente dado de alta.', 'error');
      return;
    }

    const currentY = window.scrollY || window.pageYOffset || 0;
    setSaving(true);

    try {
      const hasOpen = uci.some(isIcuEntryOpen);
      if (hasOpen) {
        showToast('Ya existe una entrada abierta a UCI. Primero registre la salida.', 'error');
        return;
      }

      await patientApi.enterPatientIcu(patientId, { enteredAt: enteredAt || undefined, reason: motivo });
      await load();
      setEnteredAt('');
      setMotivo('');
      showToast('Entrada a UCI registrada correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo registrar la entrada a UCI.', 'error');
    } finally {
      setSaving(false);
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: currentY, left: 0, behavior: 'auto' });
        });
      }
    }
  }, [enteredAt, isDischarged, load, motivo, patientId, saving, showToast, uci]);

  const salirUci = useCallback(async (event) => {
    event?.preventDefault?.();
    if (saving) return;

    const currentY = window.scrollY || window.pageYOffset || 0;
    setSaving(true);

    try {
      const abierto = uci.find(isIcuEntryOpen);
      if (!abierto) {
        showToast('No hay una entrada abierta a UCI para cerrar.', 'error');
        return;
      }

      await patientApi.leavePatientIcu(abierto.id);
      await load();
      setMotivo('');
      showToast('Salida de UCI registrada correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo registrar la salida de UCI.', 'error');
    } finally {
      setSaving(false);
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: currentY, left: 0, behavior: 'auto' });
        });
      }
    }
  }, [load, saving, showToast, uci]);

  const darDeAlta = useCallback(async () => {
    if (discharging) return;

    setDischarging(true);
    try {
      const abierto = uci.find(isIcuEntryOpen);
      if (abierto) {
        await patientApi.leavePatientIcu(abierto.id);
      }

      const res = await patientApi.updatePatient(patientId, { estado: 'alta', en_uci: false });
      setPatient(res.data);
      setDepartment(res.data?.department || '');
      await load();
      showToast('Paciente dado de alta correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo dar de alta al paciente.', 'error');
    } finally {
      setDischarging(false);
    }
  }, [discharging, load, patientId, showToast, uci]);

  const eliminarIcuEntry = useCallback(async (entryId) => {
    if (deleting) return;
    if (!window.confirm('¿Seguro que deseas eliminar esta entrada de UCI?')) return;

    setDeleting(true);
    try {
      await patientApi.deleteIcuEntry(entryId);
      await load();
      showToast('Registro de UCI eliminado correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo eliminar el registro de UCI.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [deleting, load, patientId, showToast]);

  const eliminarEvento = useCallback(async (eventId) => {
    if (deleting) return;
    if (!window.confirm('¿Seguro que deseas eliminar esta novedad?')) return;

    setDeleting(true);
    try {
      await patientApi.deletePatientEvent(eventId);
      await load();
      showToast('Novedad eliminada correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo eliminar la novedad.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [deleting, load, showToast]);

  const crearEvento = useCallback(async (event) => {
    event.preventDefault();
    if (isDischarged) {
      showToast('No se puede registrar una novedad para un paciente dado de alta.', 'error');
      return;
    }
    if (!nuevoEvento.descripcion.trim()) return;

    try {
      const payload = {
        patient: patientId,
        tipo: nuevoEvento.tipo,
        descripcion: nuevoEvento.descripcion,
        fecha: new Date().toISOString(),
      };
      const res = await patientApi.createPatientEvent(payload);
      setEventos((prev) => [res.data, ...prev]);
      setNuevoEvento({ tipo: 'estado', descripcion: '' });
      showToast('Novedad registrada correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo crear la novedad.', 'error');
    }
  }, [isDischarged, nuevoEvento.descripcion, nuevoEvento.tipo, patientId, showToast]);

  const guardarDepartamento = useCallback(async () => {
    setDeptSaving(true);
    try {
      const res = await patientApi.updatePatient(patientId, { department: department || null });
      setPatient(res.data);
      setDepartment(res.data?.department || '');
      showToast('Departamento actualizado correctamente.', 'success');
    } catch (err) {
      showToast('No se pudo actualizar el departamento.', 'error');
    } finally {
      setDeptSaving(false);
    }
  }, [department, patientId, showToast]);

  return {
    patient,
    uci,
    eventos,
    loading,
    error,
    enteredAt,
    setEnteredAt,
    motivo,
    setMotivo,
    saving,
    discharging,
    toast,
    nuevoEvento,
    setNuevoEvento,
    department,
    setDepartment,
    deptSaving,
    hasOpenEntry,
    isDischarged,
    sortedUci,
    entrarUci,
    salirUci,
    darDeAlta,
    eliminarIcuEntry,
    eliminarEvento,
    deleting,
    crearEvento,
    guardarDepartamento,
    showToast,
  };
};
